// EntityStateStoreRegistry — per-scope ref-counting for entity-state stores
// (BL-1552).
//
// WHY IT EXISTS. `EntityStateStore` is single-scope BY CONSTRUCTION: one scope id
// in the constructor, one topic, one entity map, one cursor, one transport
// unsubscribe, driven by `start()` / `stop()` from a provider's mount effect.
// That is exactly right for an auction-wide stream, where a page-level provider
// mounts one store and every consumer wants the same one.
//
// It is wrong for a PER-LOT stream. The clerk console's centre panel subscribes
// to the selected lot and lets go when the clerk moves on, so the scope belongs
// to the consuming component rather than the page — and two components rendering
// the same lot must share ONE subscription rather than opening two.
//
// THE LEDGER SIDE ALREADY HAD THIS. `LedgerStore` ref-counts subscribers per
// topic inside one store, which is why BUG-1467's per-lot bid ledger needed no
// new machinery. The LWW side had no equivalent, so BL-1552's RAC 6 — worded
// "the ref-counting STILL holds" — was net-new work rather than a regression
// guard.
//
// WHY A REGISTRY AND NOT AN ENGINE REWRITE. Making `EntityStateStore` itself
// multi-topic means moving all nine of its per-scope fields into a per-entry
// record, including BUG-1407's carefully-ordered `pending` / `publishPending` /
// `cancelPendingNotify` coalescing — on a class the registrations stream (the
// console's bidder rail) also rides. Ref-counting the store INSTANCES instead
// leaves that engine untouched, so this is the only new concurrency surface and
// the registrations rail carries no risk at all.

'use client';

import { formatRealtimeFailure } from '../../isomorphic/realtime/realtime-failure-log';

/**
 * The slice of an entity-state store this registry drives. `EntityStateStore`
 * satisfies it; the interface exists so the registry is testable without the LWW
 * engine, and so a future stream can register a differently-shaped store.
 */
export interface RegistrableEntityStore {
    /** Cold-seed + subscribe. Best-effort — implementations log their own failures. */
    start(): Promise<void>;
    /** Tear down the transport subscription. */
    stop(): void;
    /** Catch up everything changed since the store's cursor. */
    reconcile(): Promise<void>;
}

interface RegistryEntry<TStore> {
    store: TStore;
    /** Live consumers. The store is stopped and dropped when this reaches 0. */
    refs: number;
}

/**
 * Hands out shared, ref-counted entity-state stores keyed by scope id (a lot id
 * for the admin max-bid stream).
 *
 * One store per scope, created lazily on first `acquire` and stopped on last
 * `release`. Callers MUST pair every `acquire` with exactly one `release` — the
 * consuming hook does that from a `useEffect` cleanup.
 */
export class EntityStateStoreRegistry<TStore extends RegistrableEntityStore> {
    private readonly entries = new Map<string, RegistryEntry<TStore>>();

    /**
     * @param factory - builds a store for one scope. Called once per scope per
     *   live period — a scope that is fully released and re-acquired gets a FRESH
     *   store, never the stopped one (a stopped store has torn down its transport
     *   subscription, so handing it back would leave the new consumer subscribed
     *   to nothing while looking perfectly healthy).
     */
    constructor(private readonly factory: (scopeId: string) => TStore) {}

    /**
     * Take a reference to `scopeId`'s store, creating and starting it if this is
     * the first consumer.
     *
     * @param scopeId - the stream's scope (a lot id for per-lot max bids).
     * @returns the shared store — the SAME instance for every concurrent caller.
     * @sideEffect On the first reference only: constructs a store and calls
     *   `start()` (one catch-up request + one transport subscription).
     */
    acquire(scopeId: string): TStore {
        const existing = this.entries.get(scopeId);
        if (existing) {
            existing.refs++;
            return existing.store;
        }

        const store = this.factory(scopeId);
        this.entries.set(scopeId, { store, refs: 1 });
        // Fire-and-forget: `start()` is best-effort and logs its own failure (a
        // reconcile trigger retries). The `catch` is not a swallow — it stops a
        // rejected promise from surfacing as an unhandled rejection that would
        // fail an unrelated test or spam the console, and it still logs.
        void store.start().catch((err: unknown) => {
            // eslint-disable-next-line no-console
            console.error(
                ...formatRealtimeFailure(
                    '[EntityStateStoreRegistry] store start failed',
                    { scopeId },
                    err,
                ),
            );
        });
        return store;
    }

    /**
     * The live store for `scopeId`, or `undefined` — WITHOUT taking a reference.
     *
     * For readers whose lifecycle is already covered by an `acquire`/`release`
     * pair: a `useSyncExternalStore` `subscribe` / `getSnapshot` seam runs on its
     * own schedule (including during the render before the acquiring effect has
     * fired), and must not be able to create or retain a store.
     */
    peek(scopeId: string): TStore | undefined {
        return this.entries.get(scopeId)?.store;
    }

    /**
     * Drop one reference to `scopeId`'s store. Stops and forgets it when the last
     * consumer leaves; a no-op for a scope that was never acquired.
     *
     * @sideEffect On the last reference only: calls `stop()`, tearing down the
     *   transport subscription.
     */
    release(scopeId: string): void {
        const entry = this.entries.get(scopeId);
        if (!entry) return;
        entry.refs--;
        if (entry.refs > 0) return;
        this.entries.delete(scopeId);
        entry.store.stop();
    }

    /**
     * Reconcile every LIVE scope — the wake/reconnect trigger seam, matching
     * `LedgerStore.reconcileAll()`. Released scopes are gone from the map, so a
     * feed nobody is watching costs no network work.
     */
    async reconcileAll(): Promise<void> {
        await Promise.all([...this.entries.values()].map((e) => e.store.reconcile()));
    }
}
